<template>
  <div ref="checklistPrintPreview" class="checklist-print-page">
    <div v-if="isLoading" class="loading-state">Loading...</div>

    <template v-else>
      <div
        v-for="(group, groupIdx) in effectivePageGroups"
        :key="groupIdx"
        class="page"
        :class="{ 'page-continued': groupIdx > 0 }">

        <template v-if="groupIdx === 0">
          <div class="page-header">
            <img class="page-logo" src="../../../../../../assets/images/buckeye-brand.png" alt="Buckeye Transplant">
          </div>

          <div class="meta-row meta-row-main">
            <div class="meta-card">
              <div class="label">UNOS ID</div>
              <div class="value">{{ matchData?.donor?.code ?? '—' }}</div>
            </div>
            <div class="meta-card">
              <div class="label">Match ID</div>
              <div class="value">{{ matchData?.code ?? '—' }}</div>
            </div>
            <div class="meta-card">
              <div class="label">TxC</div>
              <div class="value">{{ matchData?.transplant_center?.code ?? '—' }}</div>
            </div>
          </div>

        <div class="meta-row meta-row-ver">
          <div class="meta-card">
            <div class="label">Verified By</div>
            <div class="value">{{ isVerified ? (checklist?.verified_by?.full_name ?? '—') : '—' }}</div>
          </div>
          
          <div class="meta-card">
            <div class="label">Verified At</div>
            <div class="value">{{ isVerified ? formatDt(checklist?.verified_at?.value) : '—' }}</div>
          </div>
        </div>

          <div class="meta-row meta-row-tags">
            <div class="meta-card">
              <div class="label">Applied Tags</div>
              <div class="value tag-value">
                <template v-if="matchData?.tags?.length">
                  <span v-for="t in matchData.tags" :key="t.id" class="tag-badge">{{ t.reportLabel }}</span>
                </template>
                <span v-else>No tags applied</span>
              </div>
            </div>
          </div>
        </template>

        <div class="section-label">Checklist{{ groupIdx > 0 ? ' (continued)' : '' }}: {{ checklist?.checklist_name }}</div>

        <div class="item-list">
          <div v-for="(item, itemIdx) in group" :key="item.id" class="item-card">
            <div class="item-header">
              <div class="item-num">{{ pageStartIndices[groupIdx] + itemIdx + 1 }}</div>
              <div class="item-type">{{ item.template_item?.item_type?.item_type ?? '—' }}</div>
              <div>
                <span :class="['status-badge', statusBadgeClass(item.status)]">
                  {{ statusLabel(item.status) }}
                </span>
              </div>
              <div class="meta-pair">
                <span class="mlabel">Updated</span>
                <span class="mvalue">{{ formatDt(item.status_changed_at) }} &nbsp;·&nbsp; {{ item.updated_by?.full_name ?? '—' }}</span>
              </div>
              <div class="meta-pair" v-if="hasCoordinatorSignOffEnabled">
                <span class="mlabel">Verified</span>
                <span class="mvalue">{{ formatDt(item.signed_at) }} &nbsp;·&nbsp; {{ item.signed_by?.full_name ?? '—' }}</span>
              </div>
            </div>
            <div class="item-status-history" v-if="item.status_history?.length">
              <div class="dlabel">Status History</div>
              <div class="history-entry" v-for="(entry, idx) in item.status_history" :key="idx">
                <span class="history-status">{{ statusLabel(entry.status) }}&nbsp;·&nbsp;{{ formatDt(entry.changed_at) }} &nbsp;·&nbsp; {{entry.changed_by}} </span>
              </div>
            </div>
            <div class="item-body">
              <div class="dlabel">Details</div>
              <div class="dvalue">{{ item.item_body_instance ?? '' }}</div>
            </div>
          </div>
        </div>

        <div class="doc-footer">
          <span>{{ printedAt }}</span>
          <span>Page {{ groupIdx + 1 }} of {{ effectivePageGroups.length }}</span>
        </div>
      </div>

      <button class="print-btn" @click="print">Print / Save as PDF</button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { Match } from '@/src/models/match'
import { AppliedTemplate } from '@/src/models/match/checklist/applied-template'
import { AppliedItem } from '@/src/models/match/checklist/applied-item'
// @ts-ignore
import { formatDateTimeET } from '@/src/date-helpers'
// @ts-ignore
import { useChecklistStore } from '@/src/stores/checklist'
// @ts-ignore
import { useAppCoreStore } from "@/src/stores/app-core";

const route = useRoute()
const checklistStore = useChecklistStore()
const appStore = useAppCoreStore()

// Template refs
const checklistPrintPreview = ref<HTMLElement | null>(null)

// Component state
const now = new Date()
const isLoading = ref(true)
const matchData = ref<Match | null>(null)
const appliedTemplates = ref<AppliedTemplate[]>([])
const printedAt = `Printed ${now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} at ${now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
const pageGroups = ref<AppliedItem[][]>([])

const activeChecklistId = computed<string | null>(() => {
  return (route.query.checklist_id as string) ?? null
})

const checklist = computed<any>(() => {
  const checklists = (matchData.value as any)?.checklists
  if (!checklists) return null
  if (activeChecklistId.value) {
    return checklists.find((c: any) => c.id === activeChecklistId.value) ?? null
  }
  return checklists[0] ?? null
})

const isVerified = computed<boolean>(() => !!(checklist.value?.verified_at?.value))

const allItems = computed<AppliedItem[]>(() => {
  const templates = activeChecklistId.value
    ? appliedTemplates.value.filter((t: any) => t.checklist_application_id === activeChecklistId.value)
    : appliedTemplates.value
  return templates.flatMap((t: any) => t.applied_items ?? [])
})

const effectivePageGroups = computed<AppliedItem[][]>(() => {
  return pageGroups.value.length > 0 ? pageGroups.value : [allItems.value]
})

const pageStartIndices = computed<number[]>(() => {
  let sum = 0
  return effectivePageGroups.value.map((group: AppliedItem[]) => {
    const start = sum
    sum += group.length
    return start
  })
})

const hasCoordinatorSignOffEnabled = computed(() => {
  return appStore.applicationSettings.checklist.checklist_coordinator_sign_off
})

onMounted(async () => {
  await loadData()
})

async function loadData() {
  try {
    const matchId = route.params.match_id
    const [matchResponse, templatesResponse] = await Promise.all([
      checklistStore.loadMatch(matchId),
      checklistStore.loadMatchAppliedTemplates({ match_id: matchId })
    ])
    matchData.value = Match.buildFromAPI(matchResponse)
    appliedTemplates.value = (templatesResponse.data ?? []).map((row: any) => AppliedTemplate.buildFromAPI(row))
  } finally {
    isLoading.value = false
    await nextTick()
    calcPageGroups()
  }
}

function calcPageGroups() {
  const pageEl = checklistPrintPreview.value?.querySelector('.page') as HTMLElement | null
  if (!pageEl) return
  const headerEl = pageEl.querySelector('.page-header') as HTMLElement | null
  if (!headerEl) return

  const CM = 96 / 2.54
  const printPageH = 11 * 96 - 2 * CM
  const printPageW = 8.5 * 96 - 2 * 1.2 * CM
  const pageH = printPageH * (pageEl.getBoundingClientRect().width / printPageW)

  const itemCards = Array.from(pageEl.querySelectorAll('.item-card')) as HTMLElement[]
  if (itemCards.length === 0) return

  const sectionLabelEl = pageEl.querySelector('.section-label') as HTMLElement | null
  const continuedH = (sectionLabelEl?.getBoundingClientRect().height ?? 26) + 24

  const groups: AppliedItem[][] = []
  let currentGroup: AppliedItem[] = []
  let used = itemCards[0].getBoundingClientRect().top - headerEl.getBoundingClientRect().top

  itemCards.forEach((card, i) => {
    const gap = currentGroup.length > 0 ? 10 : 0
    const cardH = card.getBoundingClientRect().height
    if (used + gap + cardH > pageH) {
      groups.push(currentGroup)
      currentGroup = [allItems.value[i]]
      used = continuedH + cardH
    } else {
      currentGroup.push(allItems.value[i])
      used += gap + cardH
    }
  })
  if (currentGroup.length > 0) groups.push(currentGroup)
  pageGroups.value = groups
}

function formatDt(datetime: string | null | undefined): string {
  if (!datetime) return '—'
  return (formatDateTimeET(datetime) ?? String(datetime)) + ' ET'
}

function statusLabel(status: string): string {
  return AppliedItem.STATUS_LABELS[status || 'not_reviewed'] ?? status
}

function statusBadgeClass(status: string): string {
  return AppliedItem.STATUS_BADGE_CLASSES[status || 'not_reviewed'] ?? 'badge-na'
}

function print() {
  window.print()
}
</script>
